<template>
  <div class="max-w-4xl mx-auto">
    <div class="bg-white rounded-lg shadow-md p-8">
      <div class="space-y-6">
        <!-- Processing Type Selection -->
        <div class="text-center space-y-6">
          <div class="space-y-2">
            <h2 class="text-2xl font-bold text-gray-900">Process Skyn Biosensor Data</h2>
          </div>
          
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <ActionButton
              @click="selectProcessingType('subject')"
              title="Process Subject"
              subtitle="Single CSV/Excel file"
              variant="primary"
              size="lg"
            >
              <template #icon>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </template>
            </ActionButton>
            
            <ActionButton
              @click="selectProcessingType('cohort')"
              title="Process Cohort"
              subtitle="Folder of CSV/Excel files"
              variant="success"
              size="lg"
            >
              <template #icon>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </template>
            </ActionButton>
          </div>
        </div>

        <!-- Prior Processed Jobs Section -->
        <PriorJobsTable
          :prior-results="priorResults"
          :loading="loadingPriorResults"
          @load-result="loadPriorResult"
          @delete-result="deletePriorResult"
          @refresh-list="fetchPriorResults"
        />
      </div>
    </div>

    <!-- Cohort Processing Coming Soon Modal -->
    <div v-if="showCohortModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3 text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mt-4">Cohort Processing Coming Soon</h3>
          <div class="mt-2 px-7 py-3">
            <p class="text-sm text-gray-500">
              Cohort processing functionality is currently under development. 
              Please use the "Process Subject" option for single file processing.
            </p>
          </div>
          <div class="items-center px-4 py-3">
            <button
              @click="closeCohortModal"
              class="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActionButton from '../components/ActionButton.vue'
import PriorJobsTable from '../components/PriorJobsTable.vue'
import axios from 'axios'
import { buildApiUrl, API_ENDPOINTS } from '../config/api.js'
import { resolveJobIdentityFromResult } from '../utils/resultsS3Key.js'
import { useLabPath } from '../composables/useLabPath.js'

export default {
  name: 'UploadChoice',
  inject: ['hideBackButton', 'updateGlobalState'],
  components: {
    ActionButton,
    PriorJobsTable
  },
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  data() {
        return {
          showCohortModal: false,
          priorResults: [],
          loadingPriorResults: false
        }
      },
  methods: {
    selectProcessingType(type) {
      if (type === 'subject') {
        this.$router.push(this.toLab('/upload/subject'))
      } else if (type === 'cohort') {
        // Show modal instead of navigating to cohort upload
        this.showCohortModal = true
        
        // TODO: Future implementation - uncomment when cohort processing is ready
        // this.$router.push('/upload/cohort')
      }
    },
    closeCohortModal() {
      this.showCohortModal = false
    },
    async fetchPriorResults() {
      this.loadingPriorResults = true
      this.priorResults = []

      try {
        const sessionId = localStorage.getItem('session_token') || 'test-session'
        const allResults = []
        const activeJobs = []

        // Fetch completed results and in-progress Batch jobs in parallel
        const listPromise = axios.post(
          buildApiUrl(API_ENDPOINTS.RESULTS_LIST),
          {
            session_id: sessionId,
            include_summary: true  // OPTIMIZATION: Request summary metadata in list call (eliminates N+1 queries)
          },
          {
            headers: { 'Content-Type': 'application/json' }
          }
        ).catch((error) => {
          console.warn('Failed to fetch prior results:', error)
          return null
        })

        const activePromise = axios.post(
          buildApiUrl(API_ENDPOINTS.PROCESS_ACTIVE),
          { session_id: sessionId },
          { headers: { 'Content-Type': 'application/json' } }
        ).catch((error) => {
          console.warn('Failed to fetch active processing jobs:', error)
          return null
        })

        const [listResponse, activeResponse] = await Promise.all([listPromise, activePromise])

        if (listResponse?.data?.success && listResponse.data.data) {
          for (const result of listResponse.data.data) {
            const { subid, dataset_id: datasetId } = resolveJobIdentityFromResult(result)
            const summary = result.summary || {}
            allResults.push({
              s3_key: result.s3_key,
              subid: subid,
              dataset_id: datasetId,
              processed_date: result.started_at,
              curve_count: summary.curve_count ?? 0,
              day_count: summary.day_count ?? 0,
              summary: summary,
              status: 'completed'
            })
          }
        }

        if (activeResponse?.data?.success && Array.isArray(activeResponse.data.data)) {
          for (const job of activeResponse.data.data) {
            activeJobs.push({
              s3_key: null,
              subid: job.subid,
              dataset_id: job.dataset_id,
              processed_date: job.submitted_at,
              curve_count: null,
              day_count: null,
              status: 'processing',
              batch_job_id: job.batch_job_id,
              batch_status: job.batch_status
            })
          }
        }

        // Group completed by subid+dataset_id, keeping most recent
        const resultsMap = new Map()
        for (const result of allResults) {
          const key = `${result.subid}_${result.dataset_id}`
          const existing = resultsMap.get(key)
          if (!existing) {
            resultsMap.set(key, result)
          } else {
            const existingDate = new Date(existing.processed_date)
            const currentDate = new Date(result.processed_date)
            if (currentDate > existingDate) {
              resultsMap.set(key, result)
            }
          }
        }

        // Prefer in-progress over completed for the same identity (reprocess in flight)
        for (const job of activeJobs) {
          const key = (job.subid != null && job.dataset_id != null)
            ? `${job.subid}_${job.dataset_id}`
            : `batch-${job.batch_job_id || Math.random()}`
          resultsMap.set(key, job)
        }

        const uniqueResults = Array.from(resultsMap.values())
        uniqueResults.sort((a, b) => {
          // Processing rows first, then by date newest first
          if (a.status === 'processing' && b.status !== 'processing') return -1
          if (b.status === 'processing' && a.status !== 'processing') return 1
          const dateA = new Date(a.processed_date || 0)
          const dateB = new Date(b.processed_date || 0)
          return dateB - dateA
        })

        this.priorResults = uniqueResults
      } catch (error) {
        console.error('Error fetching prior results:', error)
      } finally {
        this.loadingPriorResults = false
      }
    },
    async loadPriorResult(result) {
      // CRITICAL: Clear old data first to prevent mixing data from different jobs
      // Clear sessionStorage entries
      sessionStorage.removeItem('processingResults')
      sessionStorage.removeItem('configuredData')
      
      // Clear global state
      this.updateGlobalState({
        configuredData: null,
        processingResults: null,
        subid: null,
        datasetId: null,
        sessionId: null
      })
      
      // Store only metadata + s3_key (not the full processed_dataset) to avoid sessionStorage quota issues
      try {
        // If results_data is not available (fallback entry), fetch it from the API
        let resultsData = result.results_data
        if (!resultsData) {
          const sessionId = localStorage.getItem('session_token') || 'test-session'
          const detailResponse = await axios.post(
            buildApiUrl(API_ENDPOINTS.RESULTS_GET),
            {
              session_id: sessionId,
              s3_key: result.s3_key
            },
            {
              headers: { 'Content-Type': 'application/json' }
            }
          )
          
          // Parse response data if it's a string
          let responseData = detailResponse.data
          if (typeof responseData === 'string') {
            responseData = JSON.parse(responseData)
          }
          
          if (responseData.success && responseData.data) {
            resultsData = responseData.data.results || responseData.data
            result.summary = responseData.data.summary

            // Verify the data matches the expected subid/dataset
            if (resultsData?.subid && resultsData.subid !== result.subid) {
              console.warn('⚠️ WARNING: SubID mismatch! S3 key indicates subid=' + result.subid + ', but results.json has subid=' + resultsData.subid)
            }
            if (resultsData?.dataset_identifier && resultsData.dataset_identifier !== result.dataset_id) {
              console.warn('⚠️ WARNING: Dataset ID mismatch! S3 key indicates dataset_id=' + result.dataset_id + ', but results.json has dataset_identifier=' + resultsData.dataset_identifier)
            }
          } else {
            throw new Error('Failed to fetch results data from API')
          }
        }
        
        // Extract total_rows using multiple fallback strategies (for dashboard metrics)
        // Strategy 1: Use summary.data_rows from API response (most efficient - already calculated)
        // Strategy 2: Use data_metadata.total_rows if already set
        // Strategy 3: Fallback to processed_dataset.length if available in memory
        const dataMetadata = resultsData?.data_metadata || {}
        let totalRows = 0
        
        if (result.summary?.data_rows) {
          // Best option: Use pre-calculated value from API summary
          totalRows = result.summary.data_rows
        } else if (dataMetadata.total_rows) {
          // Second option: Use existing value in data_metadata
          totalRows = dataMetadata.total_rows
        } else if (resultsData.processed_dataset?.length) {
          // Fallback: Extract from processed_dataset if available in memory
          // Note: This is safe because we're reading from the API response, not sessionStorage
          totalRows = resultsData.processed_dataset.length
        }
        
        // Ensure total_rows is set in data_metadata (for displayTotalObservations)
        if (totalRows > 0) {
          dataMetadata.total_rows = totalRows
        }
        
        // Extract only metadata (small data) - exclude processed_dataset which can be 41MB+
        // NOTE: Don't store the old session_id - it won't exist in backend storage anymore
        // For prior jobs, session_id should be null to indicate this is a read-only view
        // CRITICAL: Use subid from S3 key path (result.subid) NOT from results.json file
        // The S3 key path is the source of truth: results/{subid}/{dataset_id}/results.json
        const subidFromS3Key = result.subid  // Extracted from S3 key path - this is the correct subid
        const subidFromResultsFile = resultsData?.subid  // From results.json file - may be incorrect
        if (subidFromS3Key !== subidFromResultsFile) {
          console.warn(`⚠️ SubID mismatch detected! S3 key path has subid=${subidFromS3Key}, but results.json has subid=${subidFromResultsFile}. Using S3 key path subid.`)
        }
        const metadataOnly = {
          session_id: null,  // Prior jobs don't have active sessions
          subid: subidFromS3Key,  // CRITICAL: Always use subid from S3 key path, not from results.json
          dataset_identifier: result.dataset_id,  // Also from S3 key path
          s3_key: result.s3_key, // CRITICAL: Store S3 key for on-demand fetching
          curve_features: resultsData.curve_features || [],
          day_level_data: resultsData.day_level_data || [],
          processing_summary: resultsData.processing_summary || {},
          data_metadata: dataMetadata, // Now includes total_rows if available
          data_timezone: resultsData.data_timezone || 'US/Central',
          column_metadata: resultsData.column_metadata || {},
          drinking_day_score_meta: resultsData.drinking_day_score_meta || {},
          // Applied Daily Tracking thresholds — must survive prior-job reload
          drinking_prediction_review: resultsData.drinking_prediction_review || null,
          adherence_review: resultsData.adherence_review || null,
          // Explicitly exclude processed_dataset - will be fetched on-demand via s3_key
          processed_dataset: undefined,
          is_prior_job: true  // Flag to indicate this is a prior job (read-only)
        }

        // Store metadata in sessionStorage
        sessionStorage.setItem('processingResults', JSON.stringify(metadataOnly))
        
        // Also store configured data metadata for SubjectDashboard
        // NOTE: session_id is null for prior jobs - reprocessing requires re-uploading
        // Store the s3_key as prior_job_s3_key so it can be deleted when reprocessing
        // CRITICAL: Use subid from S3 key path (result.subid) NOT from results.json file
        const configuredData = {
          session_id: null,  // Prior jobs don't have active sessions
          subid: subidFromS3Key,  // CRITICAL: Always use subid from S3 key path
          dataset_identifier: result.dataset_id,  // Also from S3 key path
          data_stored_in_s3: true, // Indicate data is in S3
          s3_key: result.s3_key,
          prior_job_s3_key: result.s3_key,  // Store for deletion when reprocessing
          is_prior_job: true  // Flag to indicate this is a prior job
        }
        sessionStorage.setItem('configuredData', JSON.stringify(configuredData))
        
        // CRITICAL: Update global state immediately so SubjectDashboard loads correct data
        // This ensures that when SubjectDashboard mounts, it gets the correct subid/dataset_id
        // instead of stale data from a previous job
        this.updateGlobalState({
          configuredData: configuredData,
          processingResults: metadataOnly,
          subid: subidFromS3Key,
          datasetId: result.dataset_id,
          sessionId: null  // Prior jobs don't have active sessions
        })

        // Navigate to subject dashboard
        this.$router.push({
          path: this.toLab('/subject-dashboard'),
          query: {
            subid: result.subid,
            dataset: result.dataset_id,
            fromPrior: 'true',
            s3_key: result.s3_key
          }
        })
      } catch (error) {
        console.error('Error loading prior result:', error)
        alert('Failed to load prior result. Please try again.')
      }
    },
    async deletePriorResult(result) {
      try {
        const sessionId = localStorage.getItem('session_token') || 'test-session'
        
        // Call API endpoint to delete from S3
        const response = await axios.post(
          buildApiUrl(API_ENDPOINTS.RESULTS_DELETE),
          {
            session_id: sessionId,
            s3_key: result.s3_key
          },
          {
            headers: { 'Content-Type': 'application/json' }
            // Note: withCredentials removed - session_id is in request body
          }
        )
        
        if (response.data.success) {
          // Remove from local state
          this.priorResults = this.priorResults.filter(r => r.s3_key !== result.s3_key)
        } else {
          throw new Error(response.data.message || 'Delete failed')
        }
      } catch (error) {
        console.error('Error deleting prior result:', error)
        alert(`Failed to delete prior result: ${error.response?.data?.message || error.message}. Please try again.`)
      }
    }
  },
  watch: {
    '$route.params.labId'() {
      // Refetch when lab changes (e.g. after logging into a different lab)
      this.fetchPriorResults()
    }
  },
  mounted() {
    // Hide back button on the initial choice page
    this.hideBackButton()
    // Fetch prior results on mount
    this.fetchPriorResults()
  }
}
</script>
